import { lazy, Suspense, type ComponentType } from "react";
import DefaultSlots from "./default";
import { type ThemeId } from "./registry";
import { useTheme } from "./ThemeProvider";
import type { SlotName } from "./types";

// default = eager (today's components, main bundle).
// elo / nike / puma = lazy chunks — only the store's theme is ever
// downloaded on the critical path (service-worker precache excludes
// assets/themes/** — see vite.config.ts).
const LazyElo = lazy(() => import("./elo"));
const LazyNike = lazy(() => import("./nike"));
const LazyPuma = lazy(() => import("./puma"));

const LAZY_THEMES: Partial<Record<ThemeId, ComponentType<{ slot: SlotName } & Record<string, unknown>>>> = {
  elo: LazyElo,
  nike: LazyNike,
  puma: LazyPuma,
};

// Reserve roughly the active slot's space while the theme chunk loads, so
// first layout stays stable (CLS rule — plan §10).
const FALLBACK_CLASS: Record<SlotName, string> = {
  header: "h-16",
  footer: "h-40",
  homePage: "min-h-screen",
  productListing: "min-h-screen",
  productDetails: "min-h-screen",
  heroBanner: "aspect-[80/33]",
  productCard: "h-64",
  sectionGrid: "min-h-[50vh]",
};

function SlotSkeleton({ slot }: { slot: SlotName }) {
  return (
    <div className={FALLBACK_CLASS[slot]} aria-hidden="true" />
  );
}

export function ThemeSlot({
  slot,
  ...props
}: { slot: SlotName } & Record<string, unknown>) {
  const { themeId } = useTheme();

  if (themeId === "default") {
    const Component = DefaultSlots[slot];
    return <Component {...props} />;
  }

  const LazyTheme = LAZY_THEMES[themeId];
  if (!LazyTheme) {
    const Component = DefaultSlots[slot];
    return <Component {...props} />;
  }

  return (
    <Suspense fallback={<SlotSkeleton slot={slot} />}>
      <LazyTheme slot={slot} {...props} />
    </Suspense>
  );
}
